import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { CREATE_TICKET } from "../graphql/mutations";

const CreateTicketDialog = ({ open, onClose, onSuccess, spaceId }) => {
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    priority: "Low",
    customerName: "",
    customerEmail: "",
  });
  const [errors, setErrors] = useState({});

  const [createTicket, { loading, error }] = useMutation(CREATE_TICKET, {
    onCompleted: () => {
      handleReset();
      onSuccess();
    },
    onError: (error) => {
      console.error("Error creating ticket:", error);
    },
  });

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null,
      });
    }
  };

  const handleReset = () => {
    setFormData({
      subject: "",
      description: "",
      priority: "Low",
      customerName: "",
      customerEmail: "",
    });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (
      formData.customerEmail &&
      !/\S+@\S+\.\S+/.test(formData.customerEmail)
    ) {
      newErrors.customerEmail = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const input = {
      subject: formData.subject.trim(),
      description: formData.description.trim(),
      priority: formData.priority,
      spaceId: spaceId || null,
    };

    // Add optional fields if provided
    if (formData.customerName.trim()) {
      input.customerName = formData.customerName.trim();
    }

    if (formData.customerEmail.trim()) {
      input.customerEmail = formData.customerEmail.trim();
    }

    createTicket({
      variables: {
        input,
      },
    });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Support Ticket</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.message}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Subject"
            value={formData.subject}
            onChange={handleChange("subject")}
            error={Boolean(errors.subject)}
            helperText={errors.subject}
            margin="normal"
            required
            autoFocus
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange("description")}
            error={Boolean(errors.description)}
            helperText={errors.description}
            margin="normal"
            multiline
            rows={4}
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              onChange={handleChange("priority")}
              label="Priority"
            >
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Urgent">Urgent</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Customer Name (Optional)"
            value={formData.customerName}
            onChange={handleChange("customerName")}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Customer Email (Optional)"
            value={formData.customerEmail}
            onChange={handleChange("customerEmail")}
            error={Boolean(errors.customerEmail)}
            helperText={errors.customerEmail}
            margin="normal"
            type="email"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? "Creating..." : "Create Ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTicketDialog;
